import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Stack,
  useTheme,
} from '@mui/material';
import CryptoJS from 'crypto-js';
import { toast, ToastContainer } from 'react-toastify';
import MFADialog from './MFADialog';
import loginLogo from '../../assets/images/login-page-logo.png';

const ENCRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY;
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4002/';
const OAUTH_TOKEN_KEY = 'oauth_consent_token';

const generateKey = () => {
  const keyBytes = CryptoJS.enc.Utf8.parse(ENCRYPTION_KEY);
  const hash = CryptoJS.SHA256(keyBytes);
  return CryptoJS.lib.WordArray.create(hash.words.slice(0, 8));
};

const encryptData = (data) => {
  if (!data) return '';
  const key = generateKey();
  return CryptoJS.AES.encrypt(data.toString(), key, {
    mode: CryptoJS.mode.ECB,
    padding: CryptoJS.pad.Pkcs7,
  }).toString();
};

const getComputerName = () => {
  const storageKey = 'browser_computer_id';
  let computerId = localStorage.getItem(storageKey);

  if (computerId && computerId.startsWith('DESKTOP-')) {
    computerId = null;
    localStorage.removeItem(storageKey);
  }

  if (!computerId) {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('Browser fingerprint', 2, 2);

    const fingerprint = [
      navigator.userAgent,
      navigator.vendor,
      navigator.platform,
      navigator.language,
      navigator.hardwareConcurrency || 'unknown',
      navigator.deviceMemory || 'unknown',
      `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`,
      window.screen.pixelDepth || window.screen.colorDepth,
      new Date().getTimezoneOffset(),
      canvas.toDataURL(),
      navigator.webdriver || 'false',
      window.chrome ? 'chrome' : window.navigator.userAgent.includes('Edg') ? 'edge' : 'other',
    ].join('|');

    computerId = CryptoJS.SHA256(fingerprint).toString(CryptoJS.enc.Hex).substring(0, 12).toUpperCase();
    localStorage.setItem(storageKey, computerId);
  }

  return computerId;
};

const OAuthAuthorize = () => {
  const theme = useTheme();
  const [searchParams] = useSearchParams();

  const clientId = searchParams.get('client_id') || '';
  const redirectUri = searchParams.get('redirect_uri') || '';
  const state = searchParams.get('state') || '';
  const scope = searchParams.get('scope') || 'conductor:read';
  const responseType = searchParams.get('response_type') || 'code';

  const [clientInfo, setClientInfo] = useState(null);
  const [scopeCatalog, setScopeCatalog] = useState([]);
  const [loadingClient, setLoadingClient] = useState(true);
  const [clientError, setClientError] = useState(null);

  const [oauthToken, setOauthToken] = useState(() => sessionStorage.getItem(OAUTH_TOKEN_KEY));
  const [step, setStep] = useState(() =>
    sessionStorage.getItem(OAUTH_TOKEN_KEY) ? 'consent' : 'login'
  );

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [mfaDialogOpen, setMfaDialogOpen] = useState(false);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaError, setMfaError] = useState('');
  const [pendingCredentials, setPendingCredentials] = useState(null);

  const paramsValid = useMemo(
    () =>
      responseType === 'code' &&
      Boolean(clientId.trim()) &&
      Boolean(redirectUri.trim()) &&
      Boolean(state.trim()),
    [responseType, clientId, redirectUri, state]
  );

  useEffect(() => {
    if (!paramsValid) {
      setLoadingClient(false);
      setClientError('Missing or invalid OAuth parameters.');
      return;
    }

    const loadClient = async () => {
      try {
        const [clientRes, scopesRes] = await Promise.all([
          fetch(`${API_BASE_URL}oauth2/client/${encodeURIComponent(clientId)}`, {
            headers: { Accept: 'application/json' },
          }),
          fetch(`${API_BASE_URL}oauth2/scopes`, {
            headers: { Accept: 'application/json' },
          }),
        ]);

        if (!clientRes.ok) {
          setClientError('Application not found or unavailable.');
          return;
        }

        const clientData = await clientRes.json();
        if (!clientData?.client_id) {
          setClientError('Application not found or unavailable.');
          return;
        }

        setClientInfo(clientData);

        if (scopesRes.ok) {
          const scopesData = await scopesRes.json();
          setScopeCatalog(Array.isArray(scopesData) ? scopesData : []);
        }
      } catch {
        setClientError('Application not found or unavailable.');
      } finally {
        setLoadingClient(false);
      }
    };

    loadClient();
  }, [clientId, paramsValid]);

  const scopeDisplay = useMemo(() => {
    const tokens = (scope || '').split(/[\s,]+/).filter(Boolean);
    if (!tokens.length) return 'None';
    const byName = Object.fromEntries(
      (scopeCatalog || []).map((s) => [s.scope_name, s.display_name || s.scope_name])
    );
    return tokens.map((t) => byName[t] || t).join(', ');
  }, [scope, scopeCatalog]);

  const completeLogin = (jwt) => {
    sessionStorage.setItem(OAUTH_TOKEN_KEY, jwt);
    setOauthToken(jwt);
    setStep('consent');
    setPassword('');
  };

  const checkMFAStatus = async (email) => {
    const response = await fetch(`${API_BASE_URL}conductor/mfa-status`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        email,
        currentComputerName: getComputerName(),
      }),
    });

    if (!response.ok) {
      throw new Error(`MFA status check failed: ${response.status}`);
    }

    const data = await response.json();
    return { mfaRequired: data.mfaRequired === true };
  };

  const performLogin = async (loginUsername, loginPassword) => {
    const response = await fetch(`${API_BASE_URL}auth/signin`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        username: loginUsername,
        password: encryptData(loginPassword),
        applicationId: 1,
        application: 'Conductor',
        currentComputerName: getComputerName(),
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      if (response.status === 401) {
        throw new Error('Invalid Credentials');
      }
      throw new Error(errorData.message || `Sign-in failed (${response.status})`);
    }

    const data = await response.json();
    if (!data?.jwt) {
      throw new Error('No token received from the server');
    }

    completeLogin(data.jwt);
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    setMfaError('');

    try {
      const tempCredentials = { username, password };
      setPendingCredentials(tempCredentials);

      const mfaResult = await checkMFAStatus(username);
      if (mfaResult.mfaRequired) {
        setSubmitting(false);
        setMfaDialogOpen(true);
        return;
      }

      await performLogin(username, password);
      setPendingCredentials(null);
    } catch (err) {
      const message = err.message || 'Login failed. Please try again.';
      setError(message);
      toast.error(message);
      setPendingCredentials(null);
    } finally {
      setSubmitting(false);
    }
  };

  const verifyMFACode = async (email, code) => {
    const response = await fetch(`${API_BASE_URL}conductor/mfa-code-verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        email,
        code,
        currentComputerName: getComputerName(),
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'MFA verification failed');
    }

    const data = await response.json();
    if (!(data.status === 'SUCCESS' && data.message === 'MFA verified - proceed to login')) {
      throw new Error(data.message || 'MFA verification failed');
    }
  };

  const handleMFAVerify = async (code) => {
    setMfaLoading(true);
    setMfaError('');
    try {
      if (!pendingCredentials) {
        throw new Error('No pending credentials found');
      }
      await verifyMFACode(pendingCredentials.username, code);
      setMfaDialogOpen(false);
      setSubmitting(true);
      await performLogin(pendingCredentials.username, pendingCredentials.password);
      setPendingCredentials(null);
    } catch (err) {
      setMfaError(err.message || 'Verification failed. Please try again.');
    } finally {
      setMfaLoading(false);
      setSubmitting(false);
    }
  };

  const redirectTo = (url) => {
    sessionStorage.removeItem(OAUTH_TOKEN_KEY);
    window.location.href = url;
  };

  const handleApprove = async () => {
    try {
      setSubmitting(true);
      const response = await fetch(`${API_BASE_URL}oauth2/authorize/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${oauthToken}`,
        },
        body: JSON.stringify({
          client_id: clientId,
          redirect_uri: redirectUri,
          state,
          scope,
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.redirect_url) {
        throw new Error(
          data.error_description || data.error || 'Authorization failed.'
        );
      }
      redirectTo(data.redirect_url);
    } catch (err) {
      toast.error(err.message || 'Authorization failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeny = async () => {
    try {
      setSubmitting(true);
      const response = await fetch(`${API_BASE_URL}oauth2/authorize/deny`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          redirect_uri: redirectUri,
          state,
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (data?.redirect_url) {
        redirectTo(data.redirect_url);
      } else {
        toast.error('Could not complete denial.');
      }
    } catch {
      toast.error('Could not complete denial.');
    } finally {
      setSubmitting(false);
    }
  };

  const cardSx = {
    p: { xs: 3, sm: 4 },
    width: '100%',
    maxWidth: 440,
    borderRadius: 3,
    border: '1px solid',
    borderColor: 'divider',
  };

  if (loadingClient) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: theme.palette.background.default,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!paramsValid || clientError) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          bgcolor: theme.palette.background.default,
        }}
      >
        <Paper sx={cardSx}>
          <Typography color="error" variant="h6">
            {clientError || 'Invalid authorization request.'}
          </Typography>
        </Paper>
      </Box>
    );
  }

  const appName = clientInfo?.name || 'An application';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
        bgcolor: theme.palette.background.default,
      }}
    >
      <ToastContainer position="bottom-right" />
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <Box
          component="img"
          src={loginLogo}
          alt="Conductor"
          sx={{ height: 40, objectFit: 'contain' }}
        />
      </Box>

      <Paper sx={cardSx} elevation={0}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
          {step === 'login' ? 'Sign in to Conductor' : 'Authorize application'}
        </Typography>

        {step === 'login' && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              <strong>{appName}</strong> wants to access your Conductor account.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleLoginSubmit}>
              <Stack spacing={2}>
                <TextField
                  label="Email"
                  type="email"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  fullWidth
                  autoComplete="username"
                />
                <TextField
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  fullWidth
                  autoComplete="current-password"
                />
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={submitting}
                  sx={{ textTransform: 'none', fontWeight: 600, py: 1.2 }}
                >
                  {submitting ? 'Signing in...' : 'Sign in'}
                </Button>
              </Stack>
            </Box>
          </>
        )}

        {step === 'consent' && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              <strong>{appName}</strong> is requesting access to your Conductor
              account.
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Permissions:</strong> {scopeDisplay}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 3, wordBreak: 'break-all' }}
            >
              <strong>Redirect URI:</strong> {redirectUri}
            </Typography>
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                fullWidth
                disabled={submitting}
                onClick={handleDeny}
                sx={{ textTransform: 'none' }}
              >
                Deny
              </Button>
              <Button
                variant="contained"
                fullWidth
                disabled={submitting}
                onClick={handleApprove}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                {submitting ? 'Authorizing...' : 'Allow'}
              </Button>
            </Stack>
          </>
        )}
      </Paper>

      <MFADialog
        open={mfaDialogOpen}
        onClose={() => {
          setMfaDialogOpen(false);
          setMfaError('');
          setPendingCredentials(null);
        }}
        onVerify={handleMFAVerify}
        email={pendingCredentials?.username}
        loading={mfaLoading}
        error={mfaError}
      />
    </Box>
  );
};

export default OAuthAuthorize;
